import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import middleware from '../Api/Middleware';
import logo from './Logo Unified SMB - Wit.svg';
import Step1ACompanyInfo from './Tabs/Step1ACompanyInfo';
import Step1BContactInfo from './Tabs/Step1BContactInfo';
import Step2Branch from './Tabs/Step2Branch';
import StepTracker from './Components/StepTracker';
import Step3LogoColor from './Tabs/Step3A';
import Step3ColorFont from './Tabs/Step3B';
import Step4Review from './Tabs/Step4Review';
import './CompanyConfig.css';
import ToastError from '../Toasts/ToastError';
import ToastSuccess from '../Toasts/ToastSuccess';

const CompanyConfig = () => {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState({
        // Step 1 - General Information
        company_name: '',
        vatnumber: '',
        cocnumber: '',
        iban: '',
        postal_code: '',
        house_number: '',
        addition: '',
        street_name: '',
        city: '',
        phone_number_country_code: '',
        phone_number: '',
        email: '',
        country: '',

        // Step 2 - Branch
        // The selected tax will be put into: default_product_tax, default_shipping_tax and default_payment_tax
        company_branch: '',
        tax: '',
        b2b_b2c: '',
        payment_term: '14',

        // Step 3 - Logo & Colors
        logo: null,
        blackwhite_logo: null,
        selectedColors: ['#ffffff', '#009FE3', '#025A7A', '#000000', '#9C9C9C'],
        font: 'Nunito'
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const updateFormData = (stepData) => {
        setFormData(prev => ({
            ...prev,
            ...stepData
        }));
    };

    const nextStep = () => {
        if (currentStep < 6) {
            setCurrentStep(prev => prev + 1);
        }
    };

    const prevStep = () => {
        if (currentStep > 1) {
            setCurrentStep(prev => prev - 1);
        }
    };

    const convertLogoToBase64 = (file, logoType) => {
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64String = reader.result;

                setFormData((prev) => ({
                    ...prev,
                    [logoType]: base64String
                }));
            };

            reader.onerror = (error) => {
                console.error(`Error converting ${logoType} to base64:`, error);
            };

            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        try {
            // Combine phone number and country code for API
            let combinedPhoneNumber = formData.phone_number;
            if (formData.phone_number_country_code && formData.phone_number) {
                combinedPhoneNumber = `+${formData.phone_number_country_code}${formData.phone_number.replace(/\s+/g, '')}`;
            }

            const submitData = {
                // Company Information
                company_name: formData.company_name,
                vatnumber: formData.vatnumber,
                cocnumber: formData.cocnumber,
                iban: formData.iban,

                // Address Information
                postal_code: formData.postal_code,
                house_number: formData.house_number,
                addition: formData.addition,
                street_name: formData.street_name,
                city: formData.city,
                country: formData.country,
                country_code_id: formData.country,
                invoice_background: formData.invoice_background,
                invoicemail: formData.invoicemail,
                invoice_streetname: formData.invoice_streetname || formData.street_name,
                invoice_house_number: formData.invoice_house_number || formData.house_number,
                invoice_addition: formData.invoice_addition || formData.addition,
                invoice_postalcode: formData.invoice_postalcode || formData.postal_code,
                invoice_city: formData.invoice_city || formData.city,

                // Contact Information
                phone_number: combinedPhoneNumber,
                email: formData.email,

                // Business Settings
                company_branch: formData.company_branch,
                default_product_tax: formData.tax,
                default_shipping_tax: formData.tax,
                default_payment_tax: formData.tax,
                b2b_b2c: formData.b2b_b2c,
                payment_term: formData.payment_term,

                // Design Settings
                email_color_1: formData.selectedColors?.[0] || '#ffffff',
                email_color_2: formData.selectedColors?.[1] || '#009FE3',
                email_color_3: formData.selectedColors?.[2] || '#025A7A',
                email_color_4: formData.selectedColors?.[3] || '#000000',
                email_color_5: formData.selectedColors?.[4] || '#9C9C9C',
                font: formData.font,
            };

            const settingsResponse = await middleware.post('settings/backoffice', submitData);

            // Get the setting_id
            const recordId = settingsResponse.data?.data?.setting_id || settingsResponse.data?.setting_id;
            if (formData.logo || formData.blackwhite_logo) {
                const logoData = {
                    setting_id: recordId,
                    logo: formData.logo || null,
                    blackwhite_logo: formData.blackwhite_logo || null
                };

                await middleware.post('settings/BackOfficeSettingsLogoOnly', logoData);
            }
            sessionStorage.clear();
            ToastSuccess('bedrijfsconfiguratie succesvol opgeslagen');
            navigate('/');
        } catch (error) {
            console.error('Error submitting form:', error);
            ToastError('Er is een fout opgetreden bij het opslaan. Probeer het opnieuw.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderStep = () => {
        switch (currentStep) {
            case 1:
                return (
                    <Step1ACompanyInfo
                        data={formData}
                        updateData={updateFormData}
                        onNext={nextStep}
                    />
                );
            case 2:
                return (
                    <Step1BContactInfo
                        data={formData}
                        updateData={updateFormData}
                        onNext={nextStep}
                        onPrev={prevStep}
                    />
                );
            case 3:
                return (
                    <Step2Branch
                        data={formData}
                        updateData={updateFormData}
                        onNext={nextStep}
                        onPrev={prevStep}
                    />
                );
            case 4:
                return (
                    <Step3LogoColor
                        data={formData}
                        updateData={updateFormData}
                        onLogoUpload={(file, type) => convertLogoToBase64(file, type)}
                        onNext={nextStep}
                        onPrev={prevStep}
                    />
                );
            case 5:
                return (
                    <Step3ColorFont
                        data={formData}
                        updateData={updateFormData}
                        onNext={nextStep}
                        onPrev={prevStep}
                    />
                );
            case 6:
                return (
                    <Step4Review
                        data={formData}
                        onSubmit={handleSubmit}
                        onPrev={prevStep}
                        isSubmitting={isSubmitting}
                    />
                );
            default:
                return (
                    <Step1ACompanyInfo
                        data={formData}
                        updateData={updateFormData}
                        onNext={nextStep}
                    />
                );
        }
    };

    return (
        <div className="multi-step-form-container">
            {/* Header Logo Area */}
            <div className="header-logo">
                <img src={logo} alt="Company Logo" className="header-logo-img" />
            </div>
            {/* Main Form Card */}
            <div className="form-card">
                {/* Step Tracker */}
                <StepTracker currentStep={currentStep} />
                {/* Step Content */}
                <div className="step-content">
                    {renderStep()}
                </div>
            </div>
        </div>
    );
};

export default CompanyConfig;